import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { LARGO_CODIGO } from '../lib/codigo';
import { guardarAlta, leerAlta } from '../lib/alta-pendiente';
import { useApp } from '../lib/contexto';
import { supabase } from '../lib/supabase';
import { Autoria, Boton, Campo, CampoCodigo, Marca, MensajeError, Tarjeta, TextoAyuda } from '../ui/base';
import { FormularioNegocio, type DatosNegocioNuevo } from '../ui/FormularioNegocio';

/**
 * Entrar y crear cuenta. El alta es correo → negocio y titular → código: verificar antes de pedir el
 * negocio dejaría cuentas de auth sin membresía. No navega por su cuenta — `verifyOtp` avisa del
 * cambio de sesión antes de resolver, y la pantalla ya está desmontada cuando su promesa vuelve.
 */
type Fase = 'correo' | 'negocio' | 'codigo';

export function Ingreso({ modo }: { modo: 'entrar' | 'crear' }) {
  const esCrear = modo === 'crear';
  const { dejarAltaVerificada } = useApp();
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [fase, setFase] = useState<Fase>('correo');
  const [datosNegocio, setDatosNegocio] = useState<DatosNegocioNuevo | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [sinCuenta, setSinCuenta] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [restaurado, setRestaurado] = useState<ReturnType<typeof leerAlta>>(null);

  /**
   * Un alta a medias sobrevive a recargar la página. Vuelve al formulario y no al paso del código
   * porque la autorización de tratamiento no se guarda y hay que marcarla otra vez (docs/08 §3).
   */
  useEffect(() => {
    if (!esCrear) return;
    const alta = leerAlta();
    if (!alta) return;
    setEmail(alta.correo);
    setRestaurado(alta);
    setFase('negocio');
  }, [esCrear]);

  // Cuenta atrás entre reenvíos del código.
  useEffect(() => {
    if (segundos <= 0) return;
    const t = setTimeout(() => setSegundos(segundos - 1), 1000);
    return () => clearTimeout(t);
  }, [segundos]);

  /** Paso 1: el correo. En el alta solo avanza; el código se pide tras capturar el negocio. */
  function siguienteDesdeCorreo(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSinCuenta(false);
    if (esCrear) {
      setFase('negocio');
      return;
    }
    void enviarCodigo();
  }

  async function enviarCodigo() {
    setOcupado(true);
    setError('');
    setSinCuenta(false);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { shouldCreateUser: esCrear },
    });
    setOcupado(false);
    if (err) {
      if (!esCrear && /signup|not allowed|not found/i.test(err.message)) {
        setSinCuenta(true);
        return;
      }
      setError('No pudimos enviar el código. Revisa el correo o inténtalo en un minuto.');
      return;
    }
    setSegundos(45);
    setFase('codigo');
  }

  /**
   * Paso 2 del alta: los datos van al contexto con su autorización, para que `/crear-negocio` no
   * pregunte otra vez, y a `localStorage` sin ella, para sobrevivir a recargar.
   */
  function guardarNegocio(datos: DatosNegocioNuevo) {
    const correo = email.trim().toLowerCase();
    setDatosNegocio(datos);
    dejarAltaVerificada({ correo, datos });
    guardarAlta(correo, datos);
    void enviarCodigo();
  }

  /** Paso 3: verificar el código. Con la sesión abierta, el enrutador decide a dónde va. */
  async function verificar(e: FormEvent) {
    e.preventDefault();
    setOcupado(true);
    setError('');
    const { error: err } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: codigo.trim(),
      type: 'email',
    });
    if (err) {
      setOcupado(false);
      setError('Código incorrecto o vencido. Pide uno nuevo si pasaron más de 10 minutos.');
      return;
    }
    setOcupado(false);
  }

  const pasos: { clave: Fase; texto: string }[] = esCrear
    ? [
        { clave: 'correo', texto: '1 · Tu correo' },
        { clave: 'negocio', texto: '2 · Tu negocio' },
        { clave: 'codigo', texto: '3 · Confirmar' },
      ]
    : [];

  /*
    Un ancho para los tres pasos y la tarjeta arriba, no centrada en el alto. La marca, el título y el
    indicador de pasos van fuera de la tarjeta; dentro va solo el paso. Reparto del registro de
    un precedente del equipo (`container-narrow` con `auth-container-top`).
  */
  return (
    <main className="flex min-h-full justify-center px-4 py-10">
      <div className="w-full max-w-3xl">
        {/* La marca es lo más grande de la pantalla: es lo único que identifica al producto aquí. */}
        <div className="mb-4 text-center">
          <Link to="/">
            <Marca className="text-marca-grande" variante="completo" />
          </Link>
          <div className="firma-degradado mx-auto mt-2 h-1 w-24 rounded-full" aria-hidden="true" />
        </div>

        <h1 className="mb-1 text-center text-titulo font-extrabold">
          {esCrear ? 'Crea tu cuenta' : 'Entra a tu negocio'}
        </h1>
        <TextoAyuda className="text-center">
          {esCrear
            ? 'Tres pasos: tu correo, los datos de tu negocio, y el código que te llega para confirmar.'
            : 'Entra con el código o enlace que llega a tu correo.'}
        </TextoAyuda>

        {esCrear && (
          <ol
            className="text-muted mb-5 flex flex-wrap items-center justify-center gap-2 text-etiqueta font-semibold"
            aria-label="Pasos del registro"
          >
            {pasos.map((p, i) => (
              <li key={p.clave} className="flex items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 ${
                    p.clave === fase ? 'bg-primary text-primary-foreground' : 'bg-surface'
                  }`}
                >
                  {p.texto}
                </span>
                {i < pasos.length - 1 && <span aria-hidden="true">→</span>}
              </li>
            ))}
          </ol>
        )}

        <Tarjeta className="w-full">
          {fase === 'correo' && (
            <form onSubmit={siguienteDesdeCorreo} className="space-y-3">
              <Campo
                etiqueta="Correo"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                data-e2e="ingreso-correo"
              />
              <Boton type="submit" grande disabled={ocupado} className="w-full" data-e2e="ingreso-enviar">
                {ocupado ? 'Enviando…' : esCrear ? 'Continuar' : 'Enviar código'}
              </Boton>
              {sinCuenta && (
                <MensajeError caja e2e="sin-cuenta">
                  No encontramos una cuenta con ese correo. Créala desde «Crea tu cuenta».
                </MensajeError>
              )}
              <p className="text-muted text-center text-contenido">
                {esCrear ? (
                  <>
                    ¿Ya tienes cuenta?{' '}
                    <Link to="/ingresar" className="text-info underline">
                      Inicia sesión
                    </Link>
                  </>
                ) : (
                  <>
                    ¿Primera vez?{' '}
                    <Link to="/crear-cuenta" className="text-info underline" data-e2e="ir-crear-cuenta">
                      Crea tu cuenta
                    </Link>
                  </>
                )}
              </p>
            </form>
          )}

          {fase === 'negocio' && (
            <FormularioNegocio
              onListo={guardarNegocio}
              ocupado={ocupado}
              onVolver={() => setFase('correo')}
              textoBoton="Continuar"
              inicial={datosNegocio ?? restaurado?.datos}
            />
          )}

          {fase === 'codigo' && (
            <form onSubmit={verificar} className="space-y-3">
              <TextoAyuda className="bg-surface rounded-control mb-0 px-3 py-2">
                Te enviamos un código de {LARGO_CODIGO} dígitos a <strong>{email}</strong>. Vence en 10 minutos.
                {!esCrear && ' El correo también trae un enlace, por si prefieres.'}
              </TextoAyuda>
              <p className="text-muted text-contenido font-semibold">Escribe el código que te llegó</p>
              <CampoCodigo
                largo={LARGO_CODIGO}
                valor={codigo}
                onCambio={setCodigo}
                etiqueta="Código que te llegó"
                e2e="codigo-ingreso"
              />
              <Boton type="submit" grande disabled={ocupado || codigo.length !== LARGO_CODIGO} className="w-full">
                {ocupado ? 'Confirmando…' : esCrear ? 'Crear mi cuenta' : 'Entrar'}
              </Boton>
              <button
                type="button"
                className="text-info w-full text-contenido underline disabled:no-underline disabled:opacity-60"
                disabled={ocupado || segundos > 0}
                onClick={() => void enviarCodigo()}
                data-e2e="reenviar-codigo"
              >
                {segundos > 0 ? `Reenviar el código (00:${String(segundos).padStart(2, '0')})` : 'Reenviar el código'}
              </button>
              <button
                type="button"
                className="text-info w-full text-contenido underline"
                onClick={() => setFase(esCrear ? 'negocio' : 'correo')}
              >
                {esCrear ? 'Volver a los datos del negocio' : 'Usar otro correo'}
              </button>
              {esCrear && (
                <TextoAyuda className="mb-0 text-center text-etiqueta">
                  Los datos de tu negocio quedan guardados.
                </TextoAyuda>
              )}
            </form>
          )}

            <MensajeError className="mt-3">{error}</MensajeError>
        </Tarjeta>
        <Autoria className="mt-8" />
      </div>
    </main>
  );
}
